import { ArrowRight } from "lucide-react";
import { useMemo } from "react";
import { useCart } from "../../context/CartContext";
import { useCustomizer } from "../../context/CustomizerContext";
import { FALLBACK_HIGHLIGHTS } from "../../data/content";
import { useProducts } from "../../hooks/useProducts";
import type { Product } from "../../types";
import { FeaturedCard, HighlightCard } from "../product/FeaturedCard";
import { Button } from "../ui/Button";
import { Reveal } from "../ui/Reveal";
import { SectionHeading } from "../ui/SectionHeading";

const COUNT = 4;

/** Um produto por categoria (como os destaques originais), completando até 4. */
const pickFeatured = (products: Product[]) => {
  const byCategory = new Map<string, Product>();
  products.forEach((p) => {
    if (!byCategory.has(p.category)) byCategory.set(p.category, p);
  });
  const picked = [...byCategory.values()].slice(0, COUNT);
  for (const p of products) {
    if (picked.length >= COUNT) break;
    if (!picked.includes(p)) picked.push(p);
  }
  return picked;
};

const slot = "w-[80%] shrink-0 snap-start sm:w-auto";

export const MaisVendidos = () => {
  const { products, loading, error } = useProducts();
  const { quantityOf } = useCart();
  const { addOrCustomize } = useCustomizer();
  const featured = useMemo(() => pickFeatured(products), [products]);
  const showFallback = error || (!loading && featured.length === 0);

  return (
    <section id="menu" aria-labelledby="destaques-title" className="relative bg-night py-24 lg:py-32">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <Reveal className="flex flex-col gap-6 sm:flex-row sm:items-end sm:justify-between">
          <SectionHeading
            id="destaques-title"
            eyebrow="Mais vendidos"
            title="Destaques do"
            highlight="Cardápio"
            description="Os burgers mais pedidos da casa"
          />
          <Button to="/cardapio" variant="outline" icon={<ArrowRight className="size-4" aria-hidden="true" />}>
            Ver cardápio completo
          </Button>
        </Reveal>

        {/* Mobile: carrossel com scroll-snap · sm: 2 colunas · lg: 4 colunas */}
        <Reveal
          stagger={0.1}
          deps={[featured.length, showFallback]}
          className="no-scrollbar -mx-4 mt-12 flex snap-x snap-mandatory scroll-px-4 gap-4 overflow-x-auto px-4 pb-4 pt-2 sm:mx-0 sm:grid sm:grid-cols-2 sm:gap-6 sm:overflow-visible sm:px-0 lg:grid-cols-4"
        >
          {loading && featured.length === 0
            ? Array.from({ length: COUNT }, (_, i) => (
                <div key={i} className={slot} aria-hidden="true">
                  <div className="h-[380px] animate-pulse rounded-card border border-line bg-card" />
                </div>
              ))
            : showFallback
              ? FALLBACK_HIGHLIGHTS.map((item) => (
                  <div key={item.id} data-reveal className={slot}>
                    <HighlightCard item={item} />
                  </div>
                ))
              : featured.map((product) => (
                  <div key={product.id} data-reveal className={slot}>
                    <FeaturedCard
                      product={product}
                      quantity={quantityOf(product.id)}
                      onAdd={() => addOrCustomize(product)}
                    />
                  </div>
                ))}
        </Reveal>
      </div>
    </section>
  );
};
